import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowRightIcon,
	BarChart3Icon,
	BellRingIcon,
	CalendarDaysIcon,
	CheckIcon,
	CreditCardIcon,
	FileTextIcon,
	LayoutGridIcon,
	StarIcon,
} from "lucide-react";
import { domAnimation, LazyMotion, useReducedMotion } from "motion/react";
import * as m from "motion/react-m";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AuroraCanvas } from "@/components/marketing/aurora-canvas";
import { FlowHub } from "@/components/marketing/flow-hub";
import { Reveal } from "@/components/marketing/reveal";

export const Route = createFileRoute("/(marketing)/")({
	component: LandingPage,
});

const ctaPrimary =
	"inline-flex h-12 items-center gap-2 rounded-full bg-linear-to-b from-[#3fc6d2] to-brand-teal px-6 font-semibold text-[15px] text-white shadow-[0_8px_22px_-6px_rgba(47,180,192,0.5),inset_0_1px_0_rgba(255,255,255,0.35)] transition-transform hover:-translate-y-0.5";
const ctaGhost =
	"inline-flex h-12 items-center gap-2 rounded-full border border-stone-200 bg-white/60 px-6 font-semibold text-[15px] text-brand-brown backdrop-blur-md transition-transform hover:-translate-y-0.5 dark:border-white/10 dark:bg-white/4 dark:text-stone-100";

const featureItems = [
	{ icon: CalendarDaysIcon, id: "agenda" },
	{ icon: LayoutGridIcon, id: "odontogram" },
	{ icon: FileTextIcon, id: "history" },
	{ icon: CreditCardIcon, id: "billing" },
	{ icon: BellRingIcon, id: "reminders" },
	{ icon: BarChart3Icon, id: "reports" },
] as const;

const statIds = ["noShows", "hoursSaved", "clinics", "uptime"] as const;
const showcasePointKeys = ["p1", "p2", "p3"] as const;

function LandingPage() {
	return (
		<LazyMotion features={domAnimation} strict>
			<Hero />
			<Features />
			<Showcase />
			<Stats />
			<Testimonial />
			<Pricing />
			<FinalCta />
		</LazyMotion>
	);
}

function Eyebrow({ children }: { children: ReactNode }) {
	return (
		<span className="inline-flex items-center gap-2 rounded-full border border-brand-teal/15 bg-brand-teal/8 px-3 py-1.5 font-semibold text-[13px] text-brand-teal tracking-wide dark:border-brand-teal/25 dark:bg-brand-teal/15">
			{children}
		</span>
	);
}

function Hero() {
	const { t } = useTranslation();

	return (
		<header className="relative overflow-hidden pt-24 pb-10">
			<AuroraCanvas className="absolute inset-0 h-[110%] w-full" />
			<div className="pointer-events-none absolute inset-0 bg-linear-to-b from-[#fdfbf6]/20 via-30% via-transparent to-[#fdfbf6] dark:from-[#14100b]/20 dark:to-[#14100b]" />

			<div className="relative mx-auto max-w-[860px] px-6 text-center">
				<Reveal>
					<Eyebrow>{t(($) => $.modules.marketing.hero.badge)}</Eyebrow>
				</Reveal>
				<Reveal delay={0.06}>
					<h1 className="mt-5 mb-4 font-bold text-[clamp(40px,6vw,74px)] text-brand-brown leading-[1.02] tracking-[-0.03em] dark:text-stone-100">
						{t(($) => $.modules.marketing.hero.titleLead)}{" "}
						<span className="bg-linear-to-r from-brand-teal via-brand-gold to-brand-teal bg-clip-text text-transparent">
							{t(($) => $.modules.marketing.hero.titleAccent)}
						</span>
					</h1>
				</Reveal>
				<Reveal delay={0.12}>
					<p className="mx-auto mb-7 max-w-[600px] text-[clamp(17px,2.2vw,20px)] text-stone-500 dark:text-stone-400">
						{t(($) => $.modules.marketing.hero.subtitle)}
					</p>
				</Reveal>
				<Reveal delay={0.18}>
					<div className="flex flex-wrap justify-center gap-3.5">
						<Link className={ctaPrimary} to="/auth/register">
							{t(($) => $.modules.marketing.hero.ctaPrimary)} <ArrowRightIcon className="size-4" />
						</Link>
						<Link className={ctaGhost} hash="odontograma" to="/">
							{t(($) => $.modules.marketing.hero.ctaSecondary)}
						</Link>
					</div>
				</Reveal>
				<Reveal delay={0.24}>
					<div className="mt-8 flex flex-wrap justify-center gap-x-7 gap-y-2 text-[13.5px] text-stone-500 dark:text-stone-400">
						<span>{t(($) => $.modules.marketing.hero.trust.noCard)}</span>
						<span>
							<b className="text-brand-brown dark:text-stone-100">
								{t(($) => $.modules.marketing.hero.trust.clinicsCount)}
							</b>{" "}
							{t(($) => $.modules.marketing.hero.trust.clinicsLabel)}
						</span>
						<span>
							★★★★★{" "}
							<b className="text-brand-brown dark:text-stone-100">
								{t(($) => $.modules.marketing.hero.trust.ratingScore)}
							</b>{" "}
							{t(($) => $.modules.marketing.hero.trust.ratingLabel)}
						</span>
					</div>
				</Reveal>
			</div>

			<Reveal delay={0.3}>
				<div className="relative mx-auto mt-13 max-w-[1000px] px-6">
					<FlowHub />
				</div>
			</Reveal>
		</header>
	);
}

function Features() {
	const { t } = useTranslation();

	return (
		<section className="px-6 py-22" id="caracteristicas">
			<div className="mx-auto max-w-[1160px]">
				<Reveal className="mx-auto mb-13 max-w-[660px] text-center">
					<Eyebrow>{t(($) => $.modules.marketing.features.eyebrow)}</Eyebrow>
					<h2 className="mt-3.5 mb-3 font-bold text-[clamp(30px,4vw,44px)] tracking-tight">
						{t(($) => $.modules.marketing.features.title)}
					</h2>
					<p className="text-[17px] text-stone-500 dark:text-stone-400">
						{t(($) => $.modules.marketing.features.subtitle)}
					</p>
				</Reveal>
				<div className="grid gap-4.5 sm:grid-cols-2 lg:grid-cols-3">
					{featureItems.map(({ icon: Icon, id }, i) => (
						<Reveal delay={(i % 3) * 0.07} key={id}>
							<div className="h-full rounded-[20px] border border-white/70 bg-white/60 p-6.5 shadow-[0_1px_0_rgba(255,255,255,0.8)_inset,0_18px_40px_-30px_rgba(20,30,70,0.4)] backdrop-blur-md transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-1 dark:border-white/10 dark:bg-white/4">
								<div className="mb-4 grid size-11.5 place-items-center rounded-[13px] bg-linear-to-br from-brand-teal/15 to-brand-gold/15 text-brand-teal">
									<Icon className="size-5.5" />
								</div>
								<h3 className="mb-2 font-semibold text-[18.5px] tracking-[-0.01em]">
									{t(($) => $.modules.marketing.features.items[id].title)}
								</h3>
								<p className="m-0 text-[14.5px] text-stone-500 dark:text-stone-400">
									{t(($) => $.modules.marketing.features.items[id].body)}
								</p>
							</div>
						</Reveal>
					))}
				</div>
			</div>
		</section>
	);
}

function FloatTag({
	className,
	color,
	children,
	delay,
}: {
	className: string;
	color: string;
	children: ReactNode;
	delay: number;
}) {
	const reduce = useReducedMotion();
	return (
		<m.div
			animate={reduce ? undefined : { y: [0, -9, 0] }}
			className={`absolute flex items-center gap-2 rounded-[12px] border border-stone-200 bg-white px-3.5 py-2.5 font-semibold text-[12.5px] shadow-[0_14px_30px_-16px_rgba(20,30,70,0.5)] dark:border-white/10 dark:bg-[#1c1712] dark:text-stone-100 ${className}`}
			transition={{ duration: 5, delay, repeat: Number.POSITIVE_INFINITY, ease: [0.22, 1, 0.36, 1] }}
		>
			<span className="size-2 rounded-full" style={{ background: color }} />
			{children}
		</m.div>
	);
}

function Showcase() {
	const { t } = useTranslation();

	return (
		<section
			className="border-stone-200 border-y bg-linear-to-b from-white to-[#f6f2e8] px-6 py-22 dark:border-white/10 dark:from-[#181310] dark:to-[#0f0c08]"
			id="odontograma"
		>
			<div className="mx-auto grid max-w-[1160px] items-center gap-14 md:grid-cols-2">
				<Reveal>
					<Eyebrow>{t(($) => $.modules.marketing.showcase.eyebrow)}</Eyebrow>
					<h2 className="mt-3.5 mb-4 font-bold text-[clamp(28px,3.6vw,40px)] tracking-[-0.02em]">
						{t(($) => $.modules.marketing.showcase.title)}
					</h2>
					<p className="text-[16.5px] text-stone-500 dark:text-stone-400">
						{t(($) => $.modules.marketing.showcase.subtitle)}
					</p>
					<ul className="mt-5.5 grid gap-3.5">
						{showcasePointKeys.map((pointKey) => (
							<li className="flex items-start gap-3 text-[15.5px]" key={pointKey}>
								<span className="grid size-6 flex-none place-items-center rounded-lg bg-brand-teal/15 text-brand-teal">
									<CheckIcon className="size-3.5" />
								</span>
								{t(($) => $.modules.marketing.showcase.points[pointKey])}
							</li>
						))}
					</ul>
					<Link className={`mt-6.5 ${ctaPrimary}`} to="/auth/register">
						{t(($) => $.modules.marketing.showcase.cta)} <ArrowRightIcon className="size-4" />
					</Link>
				</Reveal>
				<Reveal delay={0.1}>
					<div className="relative grid aspect-[1/0.86] place-items-center overflow-hidden rounded-[24px] border border-white/70 bg-white/60 shadow-[0_40px_80px_-40px_rgba(20,30,70,0.4)] backdrop-blur-md dark:border-white/10 dark:bg-white/4">
						<svg className="w-[64%] drop-shadow-[0_20px_30px_rgba(47,180,192,0.25)]" fill="none" viewBox="0 0 120 130">
							<title>{t(($) => $.modules.marketing.showcase.toothTitle)}</title>
							<defs>
								<linearGradient id="toothgrad" x1="0" x2="1" y1="0" y2="1">
									<stop offset="0" stopColor="#fff" />
									<stop offset="1" stopColor="#fbeecb" />
								</linearGradient>
							</defs>
							<path
								d="M60 14C40 6 16 12 16 40c0 22 8 30 12 52 4 18 8 24 16 24s10-16 16-16 8 16 16 16 12-6 16-24c4-22 12-30 12-52C104 12 80 6 60 14Z"
								fill="url(#toothgrad)"
								stroke="#2fb4c0"
								strokeWidth="2.4"
							/>
							<path d="M44 42c8-6 24-6 32 0" stroke="#d8a848" strokeLinecap="round" strokeWidth="2.4" />
						</svg>
						<FloatTag className="top-[12%] left-[8%]" color="#2fb4c0" delay={0}>
							{t(($) => $.modules.marketing.showcase.tags.tooth16)}
						</FloatTag>
						<FloatTag className="right-[9%] bottom-[14%]" color="#d8a848" delay={1.2}>
							{t(($) => $.modules.marketing.showcase.tags.tooth36)}
						</FloatTag>
						<FloatTag className="top-[44%] right-[6%]" color="#33261a" delay={2.1}>
							{t(($) => $.modules.marketing.showcase.tags.budget)}
						</FloatTag>
					</div>
				</Reveal>
			</div>
		</section>
	);
}

function Stats() {
	const { t } = useTranslation();

	return (
		<section className="px-6 py-22">
			<div className="mx-auto grid max-w-[1160px] gap-5 text-center sm:grid-cols-2 lg:grid-cols-4">
				{statIds.map((id, i) => (
					<Reveal delay={i * 0.07} key={id}>
						<div className="bg-linear-to-r from-brand-teal to-brand-gold bg-clip-text font-bold text-[clamp(34px,5vw,52px)] text-transparent tracking-[-0.03em]">
							{t(($) => $.modules.marketing.stats.items[id].value)}
						</div>
						<div className="mt-1 text-[14.5px] text-stone-500 dark:text-stone-400">
							{t(($) => $.modules.marketing.stats.items[id].label)}
						</div>
					</Reveal>
				))}
			</div>
		</section>
	);
}

function Testimonial() {
	const { t } = useTranslation();

	return (
		<section className="border-stone-200 border-y bg-linear-to-b from-white to-[#f6f2e8] px-6 py-22 dark:border-white/10 dark:from-[#181310] dark:to-[#0f0c08]">
			<Reveal className="mx-auto max-w-[820px] text-center">
				<Eyebrow>{t(($) => $.modules.marketing.testimonial.eyebrow)}</Eyebrow>
				<blockquote className="mt-5 mb-6 font-semibold text-[clamp(22px,3vw,30px)] leading-[1.35] tracking-[-0.02em]">
					{t(($) => $.modules.marketing.testimonial.quote)}
				</blockquote>
				<div className="flex items-center justify-center gap-3 text-[14.5px] text-stone-500 dark:text-stone-400">
					<span className="size-11 rounded-full bg-linear-to-br from-brand-teal to-brand-gold" />
					<div className="flex items-center gap-1.5">
						<StarIcon className="size-3.5 fill-brand-gold text-brand-gold" />
						<span>
							<b className="text-brand-brown dark:text-stone-100">{t(($) => $.modules.marketing.testimonial.author)}</b>{" "}
							· {t(($) => $.modules.marketing.testimonial.clinic)}
						</span>
					</div>
				</div>
			</Reveal>
		</section>
	);
}

function Pricing() {
	const { t } = useTranslation();

	const plans = [
		{
			id: "starter",
			price: "$49",
			popular: false,
			name: t(($) => $.modules.marketing.pricing.plans.starter.name),
			blurb: t(($) => $.modules.marketing.pricing.plans.starter.blurb),
			annual: t(($) => $.modules.marketing.pricing.plans.starter.annual),
			features: [
				t(($) => $.modules.marketing.pricing.plans.starter.features.f1),
				t(($) => $.modules.marketing.pricing.plans.starter.features.f2),
				t(($) => $.modules.marketing.pricing.plans.starter.features.f3),
				t(($) => $.modules.marketing.pricing.plans.starter.features.f4),
				t(($) => $.modules.marketing.pricing.plans.starter.features.f5),
				t(($) => $.modules.marketing.pricing.plans.starter.features.f6),
			],
		},
		{
			id: "professional",
			price: "$129",
			popular: true,
			name: t(($) => $.modules.marketing.pricing.plans.professional.name),
			blurb: t(($) => $.modules.marketing.pricing.plans.professional.blurb),
			annual: t(($) => $.modules.marketing.pricing.plans.professional.annual),
			features: [
				t(($) => $.modules.marketing.pricing.plans.professional.features.f1),
				t(($) => $.modules.marketing.pricing.plans.professional.features.f2),
				t(($) => $.modules.marketing.pricing.plans.professional.features.f3),
				t(($) => $.modules.marketing.pricing.plans.professional.features.f4),
				t(($) => $.modules.marketing.pricing.plans.professional.features.f5),
				t(($) => $.modules.marketing.pricing.plans.professional.features.f6),
				t(($) => $.modules.marketing.pricing.plans.professional.features.f7),
			],
		},
		{
			id: "enterprise",
			price: "$299",
			popular: false,
			name: t(($) => $.modules.marketing.pricing.plans.enterprise.name),
			blurb: t(($) => $.modules.marketing.pricing.plans.enterprise.blurb),
			annual: t(($) => $.modules.marketing.pricing.plans.enterprise.annual),
			features: [
				t(($) => $.modules.marketing.pricing.plans.enterprise.features.f1),
				t(($) => $.modules.marketing.pricing.plans.enterprise.features.f2),
				t(($) => $.modules.marketing.pricing.plans.enterprise.features.f3),
				t(($) => $.modules.marketing.pricing.plans.enterprise.features.f4),
				t(($) => $.modules.marketing.pricing.plans.enterprise.features.f5),
				t(($) => $.modules.marketing.pricing.plans.enterprise.features.f6),
			],
		},
	];

	return (
		<section className="px-6 py-22" id="precios">
			<div className="mx-auto max-w-[1160px]">
				<Reveal className="mx-auto mb-13 max-w-[660px] text-center">
					<Eyebrow>{t(($) => $.modules.marketing.pricing.eyebrow)}</Eyebrow>
					<h2 className="mt-3.5 mb-3 font-bold text-[clamp(30px,4vw,44px)] tracking-tight">
						{t(($) => $.modules.marketing.pricing.title)}
					</h2>
					<p className="text-[17px] text-stone-500 dark:text-stone-400">
						{t(($) => $.modules.marketing.pricing.subtitle)}
					</p>
				</Reveal>
				<div className="mx-auto grid max-w-[420px] items-stretch gap-5 md:max-w-none md:grid-cols-3">
					{plans.map((plan, i) => (
						<Reveal delay={i * 0.07} key={plan.id}>
							<div
								className={`relative flex h-full flex-col rounded-[22px] border bg-white/60 p-7.5 backdrop-blur-md dark:bg-white/4 ${
									plan.popular
										? "border-brand-teal/40 shadow-[0_30px_60px_-30px_rgba(47,180,192,0.45)]"
										: "border-white/70 shadow-[0_20px_44px_-34px_rgba(20,30,70,0.5)] dark:border-white/10"
								}`}
							>
								{plan.popular && (
									<span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-linear-to-br from-brand-teal to-brand-gold px-3.5 py-1.5 font-semibold text-[12px] text-white">
										{t(($) => $.modules.marketing.pricing.popularBadge)}
									</span>
								)}
								<div className="font-semibold text-[15px]">{plan.name}</div>
								<div className="mt-3 mb-0.5 font-bold text-[44px] tracking-[-0.03em]">
									{plan.price}
									<span className="font-medium text-[15px] text-stone-500 dark:text-stone-400">
										{t(($) => $.modules.marketing.pricing.perMonth)}
									</span>
								</div>
								<p className="mb-5 text-[13px] text-stone-500 dark:text-stone-400">{plan.annual}</p>
								<p className="mb-5 text-[14px] text-stone-500 dark:text-stone-400">{plan.blurb}</p>
								<ul className="mb-6 grid gap-2.75 text-[14.5px]">
									{plan.features.map((f) => (
										<li className="flex gap-2.5" key={f}>
											<CheckIcon className="size-4 flex-none text-brand-teal" />
											{f}
										</li>
									))}
								</ul>
								<Link
									className={`mt-auto w-full justify-center ${plan.popular ? ctaPrimary : ctaGhost}`}
									to="/auth/register"
								>
									{t(($) => $.modules.marketing.pricing.cta)}
								</Link>
							</div>
						</Reveal>
					))}
				</div>
			</div>
		</section>
	);
}

function FinalCta() {
	const { t } = useTranslation();

	return (
		<section className="px-6 pb-22">
			<Reveal className="mx-auto max-w-[1160px]">
				<div className="relative overflow-hidden rounded-[30px] bg-linear-to-br from-brand-teal to-brand-gold px-8 py-18 text-center text-white shadow-[0_40px_90px_-40px_rgba(47,180,192,0.6)]">
					<h2 className="mb-3.5 font-bold text-[clamp(30px,4.4vw,46px)] tracking-tight">
						{t(($) => $.modules.marketing.finalCta.title)}
					</h2>
					<p className="mx-auto mb-7 max-w-[520px] text-[17px] text-white/90">
						{t(($) => $.modules.marketing.finalCta.subtitle)}
					</p>
					<Link
						className="inline-flex h-12 items-center gap-2 rounded-full bg-white px-6 font-semibold text-[#1f7d86] text-[15px] shadow-[0_14px_30px_-10px_rgba(0,0,0,0.3)] transition-transform hover:-translate-y-0.5"
						to="/auth/register"
					>
						{t(($) => $.modules.marketing.finalCta.cta)} <ArrowRightIcon className="size-4" />
					</Link>
				</div>
			</Reveal>
		</section>
	);
}
